import React from "react";

/**
 * ════════════════════════════════════════════════════════════════════════════════
 *  상점사 포털 공용 UI 부품
 * ════════════════════════════════════════════════════════════════════════════════
 *  ※ 모든 부품은 반드시 이 파일(=컴포넌트 바깥)에 정의한다.
 *    페이지 컴포넌트 내부에 정의하면 렌더마다 새로운 타입이 만들어져 React 가 input 을
 *    리마운트하고, 한 글자 칠 때마다 포커스가 풀린다. (실제로 겪었던 버그)
 * ════════════════════════════════════════════════════════════════════════════════
 */

/** 유리판 — 이 포털의 모든 내용은 유리판 위에 얹힌다 */
/**
 * 채팅 입력칸에서 Enter 를 눌렀을 때 무엇을 할지 정한다
 *
 *   'send'    전송한다 (부르는 쪽에서 preventDefault 후 전송)
 *   'newline' 줄바꿈에 맡긴다 (preventDefault 하지 않는다)
 *   'ignore'  Enter 가 아니다
 *
 *  갈래가 셋뿐이라 화면에서 직접 판단해도 되지만, 상점사 화면과 관리자 화면
 *  두 곳이 똑같이 따라야 하는 규칙이라 한 곳에 모아두고 시험도 여기서 한다.
 */
export const chatEnterAction = (e, isTouchDevice) => {
    if (!e || e.key !== 'Enter') return 'ignore';

    /*
     * 모바일/태블릿의 ⏎ 는 언제나 '줄바꿈' 이다.
     *  기기 키보드에는 Shift+Enter 를 칠 방법이 마땅치 않아,
     *  Enter 를 전송으로 쓰면 두 줄짜리 문의를 아예 쓸 수 없다.
     *  전송은 옆의 전송버튼으로 한다.
     */
    if (isTouchDevice) return 'newline';

    /*
     * 한글 조합중(IME)에 눌린 Enter 는 글자를 확정하는 입력이지 전송이 아니다.
     *  이 갈래가 없으면 '안녕' 을 치고 Enter 로 확정하는 순간 그대로 전송된다.
     *  keyCode 229 는 isComposing 을 주지 않는 구형 브라우저용 보조판정.
     */
    if (e.nativeEvent?.isComposing || e.keyCode === 229) return 'newline';

    // PC — Enter 전송 / Shift+Enter 줄바꿈
    return e.shiftKey ? 'newline' : 'send';
};

/**
 * 한국시간(KST) 기준 인사말
 *
 *  06:00~10:59 좋은 아침입니다
 *  11:00~15:59 나른한 점심이시죠?
 *  16:00~22:59 즐거운 저녁입니다
 *  23:00~05:59 편안한 새벽입니다
 *
 *  보는 사람의 기기 시간대와 상관없이 언제나 한국시간으로 판정한다.
 *  한국은 서머타임이 없어 UTC+9 로 고정이므로, 시간대 라이브러리 없이
 *  UTC 밀리초에 9시간을 더해 나누면 어떤 환경에서도 같은 값이 나온다.
 *  (기기 시계를 쓰는 getHours() 는 해외 상점사에게 엉뚱한 인사를 한다)
 */
export const kstGreeting = (now = new Date()) => {
    const KST_OFFSET = 9 * 60 * 60 * 1000;
    const hour = Math.floor((now.getTime() + KST_OFFSET) / (60 * 60 * 1000)) % 24;
    if (hour >= 6 && hour < 11) return '좋은 아침입니다';
    if (hour >= 11 && hour < 16) return '나른한 점심이시죠?';
    if (hour >= 16 && hour < 23) return '즐거운 저녁입니다';
    return '편안한 새벽입니다';
};

export const Panel = ({children, className = '', lift = false, as: Tag = 'section', ...rest}) => (
    <Tag className={`st-glass${lift ? ' st-lift' : ''} ${className}`} {...rest}>
        {children}
    </Tag>
);

/** 섹션 제목 — eyebrow(성격) + title(이름) + desc(부연) */
export const SectionHead = ({eyebrow, title, desc, right}) => (
    <div className="tw-flex tw-items-start tw-justify-between tw-gap-4 tw-mb-5">
        <div className="tw-min-w-0">
            {eyebrow ? <p className="st-eyebrow tw-mb-1.5">{eyebrow}</p> : null}
            <h2 className="st-display tw-text-[19px] tw-text-[color:var(--st-text)]">{title}</h2>
            {desc ? (
                <p className="tw-mt-1.5 tw-text-[13px] tw-leading-relaxed tw-text-[color:var(--st-text-faint)] tw-break-keep">
                    {desc}
                </p>
            ) : null}
        </div>
        {right ? <div className="tw-flex-shrink-0">{right}</div> : null}
    </div>
);

/** 입력 한 줄 — 라벨 + 내용 + 힌트 + 오류 */
export const Field = ({label, children, error, hint, required}) => (
    <div className="tw-mb-4">
        {label ? (
            <label className="tw-mb-2 tw-flex tw-items-center tw-gap-1 tw-text-[12.5px] tw-font-semibold tw-text-[color:var(--st-text-sub)]">
                {label}
                {required ? <span className="tw-text-[color:#F0A31F]">*</span> : null}
            </label>
        ) : null}
        {children}
        {hint ? <p className="tw-mt-1.5 tw-text-[11.5px] tw-text-[color:var(--st-text-faint)]">{hint}</p> : null}
        {error ? <ErrorText>{error}</ErrorText> : null}
    </div>
);

/** 오류문구 — 사과하지 않고, 무엇이 잘못됐고 어떻게 고치는지만 말한다 */
export const ErrorText = ({children}) => (
    <p className="tw-mt-1.5 tw-flex tw-items-center tw-gap-1.5 tw-text-[12px] tw-font-medium tw-text-[color:#E0654C]">
        <span aria-hidden="true"
              className="tw-inline-block tw-h-1.5 tw-w-1.5 tw-flex-shrink-0 tw-rounded-full tw-bg-[#FF9A8B]"/>
        {children}
    </p>
);

/**
 * 버튼
 *  @param tone dawn(주) | glass(보조) | quiet(조용) | blush(위험)
 *  @param size sm | md | lg
 *  @param icon 아이콘만 있는 정사각 버튼 (좌우 패딩 없이 정사각으로 맞춘다)
 *
 *  높이·패딩·글자크기는 전부 StoreDesign.css 의 .st-btn-{size} 가 갖는다.
 *  화면마다 tw- 유틸로 제각각 조정하면 크기가 어긋나므로 여기서만 정한다.
 */
export const Btn = ({tone = 'glass', size = 'md', icon = false, className = '', busy, children, ...rest}) => (
    <button
        className={`st-btn st-btn-${tone} st-btn-${size}${icon ? ' st-btn-icon' : ''}${className ? ` ${className}` : ''}`}
        aria-busy={!!busy} {...rest}>
        {busy ? <span className={`st-spin${tone === 'dawn' ? ' st-spin-ink' : ''}`}/> : children}
    </button>
);

/**
 * 상태 배지
 *  @param tone sage(정상) | blush(주의) | lilac(정보) | dawn(강조)
 *  @param dot  앞에 점을 찍을지
 */
export const Badge = ({tone = 'lilac', dot, children}) => {
    const tones = {
        sage: 'tw-bg-[rgba(159,216,180,.18)] tw-text-[#3F8C5C] tw-border-[rgba(159,216,180,.5)]',
        blush: 'tw-bg-[rgba(255,154,139,.16)] tw-text-[#D0604A] tw-border-[rgba(255,154,139,.48)]',
        lilac: 'tw-bg-[rgba(203,182,255,.20)] tw-text-[#7457C9] tw-border-[rgba(203,182,255,.5)]',
        dawn: 'tw-bg-[rgba(255,190,72,.20)] tw-text-[#B8790F] tw-border-[rgba(255,190,72,.5)]',
    };
    const dots = {sage: '#7FC79A', blush: '#FF9A8B', lilac: '#CBB6FF', dawn: '#FFBE48'};
    return (
        <span className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-border tw-px-3 tw-py-1 tw-text-[11.5px] tw-font-bold ${tones[tone]}`}>
            {dot ? <span aria-hidden="true" className="tw-h-[6px] tw-w-[6px] tw-rounded-full"
                         style={{background: dots[tone]}}/> : null}
            {children}
        </span>
    );
};

/** 안내 박스 — 알림(info) / 주의(warn) / 완료(done) */
export const Notice = ({tone = 'info', children}) => {
    const tones = {
        info: 'tw-bg-[rgba(203,182,255,.14)] tw-border-[rgba(203,182,255,.42)] tw-text-[color:var(--st-text-sub)]',
        warn: 'tw-bg-[rgba(255,154,139,.12)] tw-border-[rgba(255,154,139,.42)] tw-text-[color:var(--st-text-sub)]',
        done: 'tw-bg-[rgba(159,216,180,.16)] tw-border-[rgba(159,216,180,.45)] tw-text-[#3F8C5C]',
    };
    return (
        <div className={`tw-rounded-[16px] tw-border tw-px-4 tw-py-3.5 tw-text-[12.5px] tw-leading-[1.75] tw-break-keep ${tones[tone]}`}>
            {children}
        </div>
    );
};

/** 확인 대화상자 — 유리판이 여명 위로 떠오른다 */
export const Dialog = ({title, children, onCancel, onConfirm, confirmLabel = '확인', cancelLabel = '취소', tone = 'dawn', busy}) => (
    <div className="tw-fixed tw-inset-0 tw-z-[9999] tw-flex tw-items-center tw-justify-center tw-p-4"
         style={{background: 'rgba(27,23,48,.46)', backdropFilter: 'blur(6px)'}}
         role="dialog" aria-modal="true">
        <Panel className="st-rise tw-w-full tw-max-w-[400px] tw-p-7">
            <h3 className="st-display tw-text-[17px] tw-text-[color:var(--st-text)]">{title}</h3>
            <div className="tw-mt-3 tw-text-[13px] tw-leading-[1.8] tw-text-[color:var(--st-text-sub)] tw-break-keep">
                {children}
            </div>
            <div className="tw-mt-7 tw-flex tw-gap-3">
                <Btn tone="glass" size="md" className="tw-flex-1" onClick={onCancel}>{cancelLabel}</Btn>
                <Btn tone={tone} size="md" className="tw-flex-1" onClick={onConfirm} busy={busy}>{confirmLabel}</Btn>
            </div>
        </Panel>
    </div>
);

/** 비어있는 화면 — 사과가 아니라 다음 행동으로 안내한다 */
export const Empty = ({title, children}) => (
    <div className="tw-py-14 tw-text-center">
        <div aria-hidden="true"
             className="tw-mx-auto tw-mb-4 tw-h-14 tw-w-14 tw-rounded-full tw-animate-breathe"
             style={{background: 'radial-gradient(circle at 34% 30%, rgba(255,190,72,.5), rgba(203,182,255,.28) 62%, transparent 72%)'}}/>
        <p className="st-display tw-text-[15px] tw-text-[color:var(--st-text)]">{title}</p>
        {children ? (
            <p className="tw-mx-auto tw-mt-2 tw-max-w-[340px] tw-text-[12.5px] tw-leading-[1.8] tw-text-[color:var(--st-text-faint)] tw-break-keep">
                {children}
            </p>
        ) : null}
    </div>
);

/** 화면 가운데 로딩 */
export const Loading = ({label = '불러오는 중'}) => (
    <div className="tw-flex tw-flex-col tw-items-center tw-gap-3 tw-py-14">
        <span className="st-spin"/>
        <p className="tw-text-[12.5px] tw-text-[color:var(--st-text-faint)]">{label}</p>
    </div>
);
